import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  BillingInformation,
  BillingMethod,
  getDashboardUrl,
  getInvoiceUrl,
  InvoiceStatus,
  Session,
  useInvoice,
  usePlan,
} from '@app/api';

import {
  Button,
  CenteredModal,
  InformationBox,
  LargeModaleWithLeftPane,
  SubmitButton,
} from '@app/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitButtonsLine } from 'components';
import { InvoiceSelection } from './InvoiceSelection';
import { FinalizeInvoiceForm } from './FinalizeInvoiceForm';
import { ChargeCardForm } from './ChargeCardForm';
import { InvoicingModalPane } from './InvoicingModalPane';

const useStyles = createUseStyles((theme: Theme) => ({
  sectionTitle: {
    ...theme.fonts.h4,
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 3,
  },
  buttonList: {
    display: 'flex',
    gap: theme.marginBase,
  },
  buttons: {
    marginTop: theme.marginBase * 2,
  },
  content: {
    padding: theme.marginBase * 4,
  },
}));

interface Props {
  session?: Session;
  isOpen: boolean;
  onRequestClose: () => any;
  billingInformation?: BillingInformation;
}

export const InvoicingModal = ({
  isOpen,
  onRequestClose,
  session,
  billingInformation,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: plan } = usePlan(session?.planId);
  const [invoiceId, setInvoiceId] = useState<string | undefined>(undefined);

  const [refetchInvoiceInterval, setRefetchInterval] = useState<number | false>(
    false,
  );
  const { data: invoice } = useInvoice(invoiceId, {
    refetchInterval: refetchInvoiceInterval,
  });

  const seeInvoice = useCallback(() => {
    if (!invoice) {
      return;
    }
    const url = getDashboardUrl(invoice);
    if (url) {
      window.open(url, '_blank', 'noopener noreferrer');
    }
  }, [invoice]);

  useEffect(() => {
    if (invoice?.status === InvoiceStatus.OPEN && refetchInvoiceInterval) {
      setRefetchInterval(false);
    }
  }, [invoice?.status, refetchInvoiceInterval]);

  if (!session) {
    return;
  }

  if (!billingInformation?.billingInformationValidated) {
    return (
      <CenteredModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={t('admin.billing.invoicing.modalTitle')}
        large
      >
        <InformationBox
          variant="danger"
          text={t('admin.billing.invoicing.billingInfoNotValidated')}
        />
        {onRequestClose && (
          <SubmitButtonsLine className={classes.buttons}>
            <Button
              text={t('admin.billing.invoicing.close')}
              small
              color="red"
              onClick={() => onRequestClose()}
            />
          </SubmitButtonsLine>
        )}
      </CenteredModal>
    );
  }

  return (
    <LargeModaleWithLeftPane
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      paneTitle={t('admin.billing.invoicing.modalTitle')}
      paneContent={
        <InvoicingModalPane
          billingInformation={billingInformation}
          session={session}
        />
      }
    >
      <div className={classes.content}>
        <InvoiceSelection
          selected={invoice}
          onSelect={(i) => setInvoiceId(i?.id)}
          session={session}
          billingInformation={billingInformation}
        />

        {invoice &&
          (billingInformation?.provider === 'STRIPE' ? (
            <InformationBox
              title={t('admin.billing.invoicing.continueOnStripe')}
            >
              <SubmitButtonsLine>
                <Button
                  text={t('admin.billing.invoicing.viewOnProvider', {
                    provider: 'Stripe',
                  })}
                  small
                  onClick={seeInvoice}
                />
              </SubmitButtonsLine>
            </InformationBox>
          ) : (
            <>
              <div className={classes.buttonList}>
                <Button
                  text={t('admin.billing.invoicing.viewOnProvider', {
                    provider: invoice.provider,
                  })}
                  small
                  onClick={seeInvoice}
                  color="purple"
                />
                {invoice && invoice?.status !== InvoiceStatus.DRAFT && (
                  <Button
                    text={t('admin.billing.invoicing.downloadInvoice')}
                    small
                    href={getInvoiceUrl(invoice)}
                    color="purple"
                    target="_blank"
                  />
                )}
              </div>
              {invoice.status === InvoiceStatus.DRAFT && (
                <FinalizeInvoiceForm
                  invoice={invoice}
                  plan={plan}
                  billingInformation={billingInformation}
                  onFinalized={() => setRefetchInterval(200)}
                />
              )}

              {invoice.status === InvoiceStatus.OPEN && (
                <div>
                  <h3 className={classes.sectionTitle}>
                    {t('admin.billing.invoicing.paymentStep')}
                  </h3>

                  {plan?.billingMethod === BillingMethod.CREDIT_CARD ? (
                    <ChargeCardForm invoice={invoice} plan={plan} />
                  ) : (
                    <InformationBox
                      text={t('admin.billing.invoicing.awaitingPayment')}
                    >
                      <SubmitButtonsLine className={classes.buttons}>
                        <SubmitButton
                          small
                          text={t('admin.billing.invoicing.paymentPage')}
                          href={`/invoice/${invoiceId}`}
                          target="_blank"
                        />
                      </SubmitButtonsLine>
                    </InformationBox>
                  )}
                </div>
              )}
            </>
          ))}
      </div>
    </LargeModaleWithLeftPane>
  );
};
